import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { secureGet, secureDelete, api, GetExcel } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "../../components/PrimaryButton";
import Filter from "../../components/Filter";
import OptionsMenu from "../../components/OptionsMenu";
import ModuleWrapper from "../../components/ModuleWrapper";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";

import AddIcon from "../../icons/plusB.svg";
import Card from "./Card";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";
import DownloadIcon from "../../icons/downloadB.svg";
import ImportIcon from "../../icons/upload.svg";
import { saveAs } from "file-saver";

const Inventaires = () => {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [repos, setRepos] = useState([]);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success", manual: false });
  const topRef = useRef(null);

  const [filters, setFilters] = useState({ needle: "", depot: "", status: "" });

  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);

  const [showModule, setShowModule] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [uploadTargetId, setUploadTargetId] = useState(null);
  const fileInputRef = useRef(null);

  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const statusOptions = useMemo(
    () => [
      { label: t("inventory_status.CREATED"), value: "CREATED" },
      { label: t("inventory_status.IN_PROGRESS"), value: "IN_PROGRESS" },
      { label: t("inventory_status.FINISHED"), value: "FINISHED" },
    ],
    [t]
  );

  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "depot",
      label: t("fields.inventory.repo"),
      type: "dropdown",
      options: repos.map((r) => ({ value: String(r.id), label: r.name })),
    },
    { name: "status", label: t("filters.orders.status"), type: "dropdown", options: statusOptions },
  ];

  useEffect(() => {
    (async () => {
      try {
        const res = await secureGet("/repos?size=1000", showSnackbar);
        setRepos(res.data.content || []);
      } catch (e) {
        console.error("Error fetching repos:", e);
      }
    })();
  }, []);

  const fetchPaginatedInventories = useCallback(
    async (page = pageNumber, size = pageSize, active = filters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("size", String(size));

        const n = (active.needle || "").trim();
        if (n) params.set("needle", n);

        const depotId = (active.depot || "").trim();
        if (depotId) params.set("repoId", depotId);

        const st = (active.status || "").trim();
        if (st) params.set("status", st);

        const res = await secureGet(`/inventories?${params.toString()}`, showSnackbar);
        const data = res?.data ?? {};
        console.log("data", data);

        const content = Array.isArray(data.content)
          ? data.content
          : Array.isArray(data)
            ? data
            : [];

        setItems(content);
        setTotalPages(Number.isFinite(data.totalPages) ? data.totalPages : 0);

        if (Number.isFinite(data.number)) setPageNumber(data.number);
        if (Number.isFinite(data.size)) setPageSize(data.size);
      } catch (e) {
        console.error("Error fetching inventories:", e);
        setItems([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageNumber, pageSize]
  );

  useEffect(() => {
    fetchPaginatedInventories(0, pageSize, { needle: "", depot: "", status: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    setPageNumber(0);
    fetchPaginatedInventories(0, pageSize, updated);
  };

  const resetFilters = () => {
    const reset = { needle: "", depot: "", status: "" };
    setFilters(reset);
    setPageNumber(0);
    fetchPaginatedInventories(0, pageSize, reset);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
    fetchPaginatedInventories(newPage, pageSize, filters);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize) => {
    const sizeNum = Number(newSize) || pageSize;
    setPageSize(sizeNum);
    setPageNumber(0);
    fetchPaginatedInventories(0, sizeNum, filters);
  };

  const handleAddClick = () => {
    setSelected(null);
    setShowModule(true);
  };

  const handleEditClick = (it) => {
    setSelected(it);
    setShowModule(true);
  };

  const handleDeleteClick = (it) => {
    setItemToDelete(it);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await secureDelete(`/inventories/${itemToDelete.id}`, showSnackbar);
      await fetchPaginatedInventories(pageNumber, pageSize, filters);
      showSnackbar(t("confirmation.inventoriesDelete"), "success");
    } catch (error) {
      console.error("Error deleting inventory:", error);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const handleModuleClose = (action = "cancel") => {
    if (action === "submit") fetchPaginatedInventories(pageNumber, pageSize, filters);
    setShowModule(false);
    setSelected(null);
  };

  const handleDownloadClick = async (inventory) => {
    try {
      const response = await GetExcel(`/inventories/${inventory.id}/final-stock/export`, showSnackbar);
      const today = new Date().toISOString().split("T")[0];
      const fileName = `Rapport_${inventory.name}_${today}.xlsx`;
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, fileName);
      showSnackbar(t("confirmation.downloadSuccess"), "success");
    } catch (error) {
      console.error("Error downloading stock report:", error);
      showSnackbar(t("errors.generic"), "error");
    }
  };

  const beginUpload = (inventoryId) => {
    setUploadTargetId(inventoryId);
    fileInputRef.current?.click();
  };

  const handleUpload = async (file, inventoryId) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/inventories/${inventoryId}/initial-stock/import`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: localStorage.getItem("token"),
        },
      });
      fetchPaginatedInventories(pageNumber, pageSize, filters);
      showSnackbar(t("confirmation.upload"), "success");
    } catch (error) {
      console.error("File upload failed:", error);
      showSnackbar(t("errors.generic"), "error");
    }
  };

  const getMenuOptions = (it) => [
    { label: t("actions.modify"), icon: EditIcon, action: () => handleEditClick(it) },
    { label: t("actions.importStock"), icon: ImportIcon, action: () => beginUpload(it.id) },
    ...(it.status === "FINISHED"
      ? [{ label: t("actions.downloadStockReport"), icon: DownloadIcon, action: () => handleDownloadClick(it) }]
      : []),
    { label: t("actions.delete"), icon: DeleteIcon, color: "#EF476F", action: () => handleDeleteClick(it) },
  ];

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.inventory")}</title>
      </Helmet>

      {/* Header */}
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t("titles.inventory")}</h1>
        <div className="ml-auto text-[16px] flex gap-3">
          <PrimaryButton
            label={t("primaryButtons.add_inventory")}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      {/* Filters */}
      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      {/* Content with loading overlay (like Depots) */}
      <div >
        {loading && (

          <LoadingLogo />
        )}

        <div className="mt-6 grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {(
            items.map((it) => (
              <Card
                key={it.id}
                label={(it.repoName || "").toUpperCase()}
                title={it.name || ""}
                status={it.status}
                module="inventory"
                optionsMenu={
                  <div onClick={(e) => e.stopPropagation()}>
                    <OptionsMenu options={getMenuOptions(it)} />
                  </div>
                }
              />
            ))
          )}
        </div>
      </div>

      {/* Pagination */}
      <div>
        <PageScroller
          pageNumber={pageNumber}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      {/* Module */}
      {showModule && (
        <ModuleWrapper
          moduleType="inventories"
          mode={selected ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, "success")}
          initialValues={selected || {}}
        />
      )}

      {/* Delete */}
      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Snackbar */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && uploadTargetId) {
            handleUpload(file, uploadTargetId);
          }
          setUploadTargetId(null);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default Inventaires;
